import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getDashboardThemeClasses } from '../dashboard/dashboardTheme.ts';

interface TemplateDialogProps {
  title: string;
  children: React.ReactNode;
  isDark: boolean;
  busy?: boolean;
  onClose: () => void;
}

/** Local Dialog follows the app's portal, focus-trap, Escape and focus-return contract. */
export function TemplateDialog({ title, children, isDark, busy, onClose }: TemplateDialogProps) {
  const titleId = useId();
  const dialog = useRef<HTMLElement>(null);
  const theme = getDashboardThemeClasses(isDark);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => { document.body.style.overflow = originalOverflow; previous?.focus(); };
  }, []);
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && !busy) { event.preventDefault(); event.stopPropagation(); onClose(); }
    if (event.key === 'Tab') {
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]') || []);
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
      if (!focusable.length) event.preventDefault();
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onKeyDown={onKeyDown} onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <section ref={dialog} role="dialog" tabIndex={-1} aria-modal="true" aria-labelledby={titleId} aria-busy={busy} className={`max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl border p-5 shadow-xl outline-none ${theme.surface} ${theme.primaryText}`}>
        <div className="mb-5 flex items-center justify-between gap-4"><h2 id={titleId} className="text-lg font-medium">{title}</h2><button type="button" onClick={onClose} disabled={busy} aria-label="Fechar diálogo" className="rounded-lg p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50"><X size={18} /></button></div>
        {children}
      </section>
    </div>, document.body,
  );
}
